import { type Question } from './Question/Question';
import { QuestionFactory } from './Question/QuestionFactory';
import {
    type ActivityType,
    type FormInput,
    type InfoBox,
    type SupportFormData,
    type SupportStepData,
    type TicketActivityStates,
} from './support.type';
import { formFieldFactory } from './form-fields/formFieldFactory';
import { type FormField } from './form-fields/FormField';

export class SupportStep {
    workflowInstanceId: string;
    activityId: string;
    uniqueId: string;
    sessionId: number;
    activityName: string;
    activityType: ActivityType;
    ticketActivityState?: TicketActivityStates;
    needFurtherReviewButton?: boolean;
    hasReturnButton?: boolean;
    activityTitle: string;
    activitySubTitle: string;
    activityDescription: string;
    private readonly _welcomeText: string;
    infoBoxes: InfoBox[];
    questionOptions: Question[];
    _selectedQuestion?: Question;
    formInputs: FormInput[];
    questionText: string;
    token: string;
    canBackToPreviousActivityAfterExecution: boolean;
    modifiedAt: Date;
    submitButtonText?: string;
    needLogin: boolean;
    readonly isFinal: boolean;
    private readonly _form: FormField<any>[] = [];
    constructor(
        step: SupportStepData,
        public readonly order: number
    ) {
        this.submitButtonText = step.submitButtonText;
        this.workflowInstanceId = step.workflowInstanceId;
        this.activityId = step.activityId;
        this.uniqueId = step.uniqueId;
        this.sessionId = step.sessionId;
        this.activityName = step.activityName;
        this.activityType = step.activityType;
        this.activityTitle = step.activityTitle;
        this.activitySubTitle = step.activitySubTitle;
        this.activityDescription = step.activityDescription;
        this._welcomeText = step.welcomeText;
        this.needLogin = !!step.needLogin;
        this.infoBoxes = step.infoBoxes;
        this.needFurtherReviewButton = step.needFurtherReviewButton;
        this.hasReturnButton = step.hasReturnButton;
        this.ticketActivityState = step.ticketActivityState;
        this.questionOptions =
            step.questionOptions?.map(question => QuestionFactory(question)) ||
            [];
        const selectedQuestion = this.questionOptions.find(
            question => question.isSelected
        );
        if (selectedQuestion) {
            this._selectedQuestion = selectedQuestion;
        }
        this.isFinal = !!step.isWorkflowFinalStep;
        this.formInputs = step.formInputs || [];
        this.questionText = step.questionText;
        this.token = step.token;
        this.canBackToPreviousActivityAfterExecution =
            step.canBackToPreviousActivityAfterExecution;
        this.modifiedAt = new Date(step.modifiedAt);
        this._form =
            step.formInputs?.map(input => formFieldFactory(input)) ?? [];
    }

    get selectedQuestion() {
        return this._selectedQuestion;
    }

    get isStart() {
        return this.order === 0 && !this.isFinal;
    }

    get welcomeText() {
        if (!this.isStart) {
            return undefined;
        }
        return this._welcomeText;
    }

    selectQuestion(questionId: number) {
        this._selectedQuestion = this?.questionOptions.find(
            ({ key }) => key === questionId
        );
    }

    private get selectedQuestionData() {
        if (!this.selectedQuestion) {
            return [];
        }
        return [
            {
                key: this.selectedQuestion.key,
                value: this.selectedQuestion.value,
            },
        ];
    }

    getFormData(): SupportFormData {
        const formValidations = this.form.map(field =>
            field.validateCurrentField()
        );
        // Fix: read from the invalid field inside the question
        let questionOptionValidation: string | true = true;
        if (this.selectedQuestion?.isEditable()) {
            questionOptionValidation =
                this.selectedQuestion?.updateCurrentValue();
        }

        const firstError =
            formValidations.find(form => form !== true) ??
            (questionOptionValidation === true
                ? undefined
                : questionOptionValidation);

        if (firstError) {
            throw new Error(firstError, {
                cause: 'custom',
            });
        }
        return {
            ActivityId: this.activityId,
            WorkflowInstanceId: this.workflowInstanceId,
            Items: [
                ...this.form.map(({ data, key }) => ({ value: data, key })),
                ...this.selectedQuestionData,
            ],
        };
    }

    get form() {
        return this._form;
    }
}
